import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PaginationContainer } from "src/components/ListScreen";
import { Pagination } from "src/components/Pagination";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { colors } from "src/styles";
import { DBVendor, ID } from "src/types";
import styled from "styled-components";
import { NumberParam, useQueryParam } from "use-query-params";
import { DEFAULT_PAGE_SIZE, INITIAL_LAST_PAGE } from "../Clients/constants";
import { setOpenedVendorsPage as setPageStore } from "./../../redux/actions/openedPages";
import { vendorsTableHeaders } from "./constants";
import { VendorsTable } from "./VendorsTable";

const Vendors = () => {
  const [lastPage, setLastPage] = useState<number>(INITIAL_LAST_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [openedVendorsPage, setOpenedVendorsPage] = useQueryParam("openedVendorsPage", NumberParam);
  const [isLoading, setIsLoading] = useState(false);
  const [vendors, setVendors] = useState<DBVendor[]>([]);

  const [currentVendorsPage, setCurrentVendorsPage] = useState<DBVendor[]>([]);
  const pageNum = openedVendorsPage || 1;
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { id } = useSelector(getUserData);

  useEffect(() => {
    setIsLoading(true);
    getPageSize().finally(() => getVendors());
  }, [pageSize]);
  const getPageSize = async () => {
    try {
      const { data } = await FirebaseDataService.getEmployeeVendorsPageSize();
      setPageSize(data || DEFAULT_PAGE_SIZE);
    } catch (e) {
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  };
  const getVendors = async () => {
    setIsLoading(true);

    if (!lastPage || (lastPage && pageNum <= lastPage)) {
      try {
        const { data: allVendors } = await FirebaseDataService.getEmployeeVendors(id.toString());
        const resultVendorsPageArray = allVendors.slice((pageNum - 1) * pageSize, pageNum * pageSize);
        setVendors(allVendors);
        setCurrentVendorsPage(resultVendorsPageArray);
        setLastPage(Math.ceil(allVendors.length / pageSize));
      } catch (e) {
        console.log(e.message);
      }
    }

    setIsLoading(false);
  };
  const onPageChangeClick = (pageNumber: number) => {
    setOpenedVendorsPage(pageNumber);
    dispatch(setPageStore(pageNumber));
    setCurrentVendorsPage(vendors.slice((pageNumber - 1) * pageSize, pageNumber * pageSize));
  };

  const onRowClick = (vendorID: ID) => navigate(`/vendors/${vendorID}`);

  const vendorsWithLogos = vendors.filter((vendor) => !!vendor.logo).length;
  const vendorsWithAddress = vendors.filter((vendor) => vendor.address?.trim().length).length;
  const distinctAddresses = new Set(vendors.map((vendor) => vendor.address?.trim()).filter(Boolean)).size;

  return (
    <Page>
      <Header>
        <Eyebrow>Resources</Eyebrow>
        <TitleRow>
          <Title>Vendors</Title>
          <Meta>{`${vendors.length} vendor${vendors.length === 1 ? "" : "s"} on record`}</Meta>
        </TitleRow>
      </Header>

      <TilesGrid>
        <SummaryCard>
          <SummaryLabel>Total Vendors</SummaryLabel>
          <SummaryValue>{vendors.length}</SummaryValue>
          <SummarySub>active vendor records</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>With Logos</SummaryLabel>
          <SummaryValue>{vendorsWithLogos}</SummaryValue>
          <SummarySub>branded supplier profiles</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Addressed</SummaryLabel>
          <SummaryValue>{vendorsWithAddress}</SummaryValue>
          <SummarySub>records with location data</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Unique Addresses</SummaryLabel>
          <SummaryValue>{distinctAddresses}</SummaryValue>
          <SummarySub>distinct supplier locations</SummarySub>
        </SummaryCard>
      </TilesGrid>

      <Panel>
        <PanelHeader>
          <PanelTitle>All Vendors</PanelTitle>
          <PanelMeta>{`${currentVendorsPage.length} shown`}</PanelMeta>
        </PanelHeader>

        <VendorsTable
          onRowClick={onRowClick}
          isLoading={isLoading}
          headers={vendorsTableHeaders}
          data={currentVendorsPage}
        />
      </Panel>

      <PaginationContainer>
        <Pagination
          totalCount={vendors.length}
          pageSize={pageSize}
          onPageChange={onPageChangeClick}
          currentPage={pageNum}
        />
      </PaginationContainer>
    </Page>
  );
};

export default Vendors;

const Page = styled.div`
  padding: 24px 40px 0;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;

const Title = styled.h1`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 32px;
  font-weight: 700;
`;

const Meta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
  padding: 16px;
`;

const SummaryLabel = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const SummaryValue = styled.p`
  margin: 6px 0 2px;
  color: ${colors.black.dark1};
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
`;

const SummarySub = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const Panel = styled.section`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.grey.light3};
  padding: 12px 16px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PanelMeta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;
